import { isNull, noop } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import { bespokeFormatter, commissionedByFormatter } from '../../../../shared/table/arkTableFormatters';
import { OpinionAverage, clearOpinionAverageTableFilters, getOpinionAverageColumnSort, getOpinionAverageFilters, getOpinionsWithAverage, redirectToOpinion, setOpinionAverageTableColumnSort, setOpinionAverageTableFilters } from '../../store';
import { getAverageLabel } from './AverageLevel';
import { ArkTableColumn, ColumnSort, TableFilterType } from '../../../../shared/table/types';
import { ArkTable } from '../../../../shared/table/ArkTable';
import { DropdownOption } from '../../../../shared/dropdown/Dropdown';
import { getAllDropdownLists } from '../../../../admin/dropdown-lists/store';
import { allFocusOptions, opinionCommissionedByOptions } from '../../../../constants/opinion';

const averageFormatter = (average: number) => {
    const { colour, label } = getAverageLabel(average);
    return [colour, label];
};

interface AverageTableProps {
    height: number;
    width: number;
    fetchOpinionBreakdown: (opinionId: number) => void;
    selectOpinion: (opinion: OpinionAverage) => void;
    selectedOpinion: OpinionAverage | null;
    chartIsFullScreen?: boolean;
}

export const averageOptions: DropdownOption[] = [
    { value: '1', label: 'Highly Desirable' },
    { value: '0.8', label: 'Desirable' },
    { value: '0.6', label: 'Acceptable' },
    { value: '0.4', label: 'Of Concern' },
    { value: '0.2', label: 'Of Serious Concern' }
];

const TITLE_OFFSET = 40;
const FULL_SCREEN_FOOTER_OFFSET = 40;

export const AverageTable: React.FC<AverageTableProps> = ({ height, width, fetchOpinionBreakdown, selectedOpinion, selectOpinion, chartIsFullScreen = true }) => {
    const dispatch = useAppDispatch();
    const opinionsWithAverage = useAppSelector(getOpinionsWithAverage);
    const filters = useAppSelector(getOpinionAverageFilters);
    const columnSort = useAppSelector(getOpinionAverageColumnSort);
    const dropdownLists = useAppSelector(getAllDropdownLists);

    const toggleColumnSort = useCallback((columnSort: ColumnSort) => dispatch(setOpinionAverageTableColumnSort(columnSort)), [dispatch]);
    const updateFilter = useCallback((key: string, value: string | string[] | null, type: keyof TableFilterType = 'text') => dispatch(setOpinionAverageTableFilters(key, value, type)), [dispatch]);
    const clearAllFilters = useCallback(() => dispatch(clearOpinionAverageTableFilters()), [dispatch]);

    const showBreakdown = useCallback((data: OpinionAverage) => {
        if (chartIsFullScreen) {
            fetchOpinionBreakdown(data.opinionId);
        }
    }, [fetchOpinionBreakdown, chartIsFullScreen]);

    const tableWidth = useMemo(() => chartIsFullScreen ? width / 2 : width, [width, chartIsFullScreen]);
    const tableHeight = useMemo(() => height - (chartIsFullScreen ? (FULL_SCREEN_FOOTER_OFFSET + TITLE_OFFSET) : TITLE_OFFSET), [height, chartIsFullScreen]);
    const selectedTableRow = useMemo(() => !isNull(selectedOpinion) ? { field: 'opinionId', fieldValue: selectedOpinion.opinionId } : undefined, [selectedOpinion]);

    const redirect = useCallback((row: OpinionAverage) => dispatch(redirectToOpinion(row.location, row.opinionId)), [dispatch]);

    const opinionJurisdictionOptions = useMemo(() => {
        const jurisdictionList = dropdownLists.find(({ name }) => name === 'OpinionJurisdiction');
        return jurisdictionList ? jurisdictionList.options.map(type => ({ value: type, label: type })) : [];
    }, [dropdownLists]);

    const bespokeOptions = useMemo(() => [{ label: 'Bespoke', value: '1' }, { label: 'Industry Standard', value: '0' }], []);

    const commissionedByOptions = useMemo(() => opinionCommissionedByOptions(), []);

    const filterDropdownOptions = useMemo(() => ({
        bespoke: bespokeOptions,
        commissionedBy: commissionedByOptions,
        jurisdiction: opinionJurisdictionOptions,
        focus: allFocusOptions,
        average: averageOptions
    }), [bespokeOptions, opinionJurisdictionOptions, commissionedByOptions]);

    const columnDefs: ArkTableColumn[] = [
        { id: 'jurisdiction', header: 'Jurisdiction', field: 'jurisdiction', width: 0.25, component: 'tooltip', canSort: true, canFilter: true },
        { id: 'focus', header: 'Focus', field: 'focus', width: 0.15, component: 'tooltip', canSort: true, canFilter: true },
        { id: 'bespoke', header: 'Bespoke/Industry Standard', field: 'bespoke', width: 0.2, valueFormatter: bespokeFormatter, canSort: true, canFilter: true },
        { id: 'commissionedBy', header: 'Commissioned By', field: 'commissionedBy', width: 0.15, valueFormatter: commissionedByFormatter, component: 'tooltip', canSort: true, canFilter: true },
        { id: 'average', header: 'Score', field: 'average', width: 0.15, component: 'colour', valueFormatter: averageFormatter, canSort: true, canFilter: true },
        { id: 'opinionLink', header: 'Go', field: 'jurisdiction', width: 0.1, component: 'redirect', redirect }
    ];

    return (
        <div style={{ marginLeft: chartIsFullScreen ? '10px' : 0 }}>
            <ArkTable
                colDefs={columnDefs}
                rowData={opinionsWithAverage}
                testId='opinions-average'
                page={1}
                total={opinionsWithAverage.length}
                next={noop}
                previous={noop}
                onRowClick={row => chartIsFullScreen && selectOpinion(row as OpinionAverage)}
                onRowDoubleClicked={row => chartIsFullScreen && showBreakdown(row as OpinionAverage)}
                toggleSort={toggleColumnSort}
                pageSize={opinionsWithAverage.length}
                updatePageSize={noop}
                showPageSize={false}
                width={tableWidth}
                height={tableHeight}
                selectedRow={selectedTableRow}
                clearAllFilters={clearAllFilters}
                filters={filters}
                updateFilter={updateFilter}
                removeTextFilter={['bespoke', 'focus', 'commissionedBy', 'average']}
                columnSort={columnSort}
                filterDropdownOptions={filterDropdownOptions}
                showPaginationOptions={false}
            />
        </div>
    );
};
